import { type RPCClient } from "./client";

export interface RPCTarget {
    postMessage: MessagePort["postMessage"];
    addEventListener: MessagePort["addEventListener"];
    removeEventListener: MessagePort["addEventListener"];
    close?: () => void
}

export function dbgRPCPort(prefix: string, port: MessagePort): MessagePort {
    return new Proxy(port, {
        get(target: MessagePort, prop: keyof MessagePort) {
            return (...params: any[]) => {
                try {
                    const func = target[prop] as any;
                    if (!func) {
                        return func;
                    }
                    const result = func.call(target, ...params);
                    console.log(`[${prefix} PORT] ${prop}:`, params, result);
                    return result;
                } catch (e) {
                    console.error(`[${prefix} PORT] ${prop}:`, params, e);
                    throw e;
                }
            };
        }
    });
}

export function sleep(time: number): Promise<void> {
    return new Promise(rs => setTimeout(rs, time));
}


export function createPortPair(): [MessagePort, MessagePort] {
    const port = new MessageChannel();
    return [port.port1, port.port2];
}


export function raceSignal(...signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();
    const signal = controller.signal;

    function forward(a: Event) {
        controller.abort();
        for (let sig of signals) {
            sig.removeEventListener("abort", forward);
        }
    }

    for (let sig of signals) {
        sig.addEventListener("abort", forward);
    }

    return signal;
}

const global = self;
const isSharedArrayBuffer = (obj: any) => {
    if (!global["SharedArrayBuffer"]) return false;
    return obj instanceof global.SharedArrayBuffer;
}

export function findTransferables(obj: any): Transferable[] {
    function _find(current: any, ary: any[]) {
        if (typeof current !== "object") {
            return;
        } else if (current === null || current === undefined) {
            return;
        } else if (current instanceof Array || !!current[Symbol.iterator]) {
            for (let item of current) {
                _find(item, ary);
            }
        } else if (current instanceof Map) {
            for (let v of current.values()) {
                _find(v, ary);
            }
        } else if (current instanceof ArrayBuffer && !(isSharedArrayBuffer(obj))) {
            ary.push(current);
        } else if (ArrayBuffer.isView(current)) {
            _find(current.buffer, ary);
        } else if (current instanceof MessagePort) {
            ary.push(current);
        } else {
            for (let v of Object.values(current)) {
                _find(v, ary);
            }
        }
    }

    const result: any[] = [];
    _find(obj, result);
    return result;
}

interface TransferredWrapped {
    type: "null"|"undefined"|"custom"|"raw"|"promise",
    value: any
}
export interface Wrapped extends TransferredWrapped {
    post: () => void
}

interface TransferredRpcWrapper {
    type: string
    value: any
}
export interface RpcWrapper extends TransferredRpcWrapper {
    type: string,
    value: any;
    post: () => void
}

const types: Map<string, (wrapped: TransferredRpcWrapper) => any> = new Map();
types.set("proxy", (wrapped: TransferredRpcWrapper) => {
    const { RPCClient } = require("./client");

    const source: MessagePort = wrapped.value;
    source.start();

    return new RPCClient(source);
});


export interface Wrappable {
    rpcWrap(): RpcWrapper;
}


function _wrap(value: any, posts: (()=>void)[]): TransferredWrapped {
    if (value === null) {
        return {type: "null", value: null}
    } else if (value === undefined) {
        return {type: "undefined", value: null}
    } else if (!!value["then"] && typeof value.then === "function") {
        const {port1, port2} = new MessageChannel();
        value.then(
            (v: any) => {
                const {post, ...wrappable} = wrap(v);
                port1.postMessage({"type": "resolve", "value": wrappable}, findTransferables(wrappable));
                port1.close();
                if (!!post) post();
            },
            (v: any) => {
                const {post, ...wrappable} = wrap(v);
                port1.postMessage({"type": "reject", "value": wrappable}, findTransferables(wrappable));
                port1.close();
                if (!!post) post();
            },
        )
        port1.start();
        posts.push(() => {port2.close();})
        return {
            type: "promise",
            value: port2
        }
    } else if (value instanceof Error) {
        return {
            type: "raw",
            value: value
        }
    } else if (!!value.rpcWrap) {
        const {value: val, type, ...extra} = value.rpcWrap();
        if (!!extra["post"])
            posts.push(extra["post"]);

        return {
            type: "custom",
            value: {
                type,
                data: _wrap(val, posts)
            },
        }
    } else {
        return {
            type: "raw",
            value
        }
    }
}

export const reportErrorGlobally = (e: any) => (self.reportError || self.console.error)(e)
export function wrap(value: any): Wrapped {
    const posts: (()=>void)[] = []
    const partial = _wrap(value, posts);
    return {
        ...partial,
        post() { posts.forEach((f) => {try { f(); } catch (e) {reportErrorGlobally(e)}}) }
    }
}

export function unwrap(value: TransferredWrapped): any {
    if (value.type === "null") return null;
    if (value.type === "undefined") return undefined;
    if (value.type === "custom") {
        const unwrapper = types.get(value.value.type);
        if (!unwrapper) throw new Error("Unknown unwrapper.");
        return unwrapper(value.value.value);
    }
    if (value.type === "promise") {
        const port: MessagePort = value.value;
        return new Promise((rs, rj) => {
            port.addEventListener("messageerror", () => {
                rj(new Error("Something went wrong."));
                port.close();
            });
            port.addEventListener("message", (msg: MessageEvent<{type: "resolve"|"reject", value: TransferredWrapped}>) => {
                const func = msg.data.type === "reject" ? rj : rs;
                func(unwrap(msg.data.value));
                port.close();
            });
            port.start();
        });
    }
    if (value.type === "raw") {
        return value.value;
    }
}


export type RequestPacket =
      {type: "call", id: string, name: string, params: any[]}
    | {type: "close" };


export type ResponsePacket =
    | {type: "result", id: string, data: any}
    | {type: "error", id: string, reason: string};


export type EventPacket =
      {type: "error", reason: string}
    | {type: "event", data: any};
